import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import ArgonBox from "components/ArgonBox";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { toast } from "react-toastify";
import api from "utils/api";
import BannerForm from "Views/Banner/BannerForm";
import {
  appendBannerFields,
  emptyBannerForm,
  toDateTimeLocal,
  validateBannerForm,
} from "Views/Banner/bannerOptions";

function EditBanner() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [formData, setFormData] = useState(emptyBannerForm);
  const [bannerImage, setBannerImage] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);

  useEffect(() => {
    const fetchBannerData = async () => {
      try {
        const response = await api.get(`/banners/${id}`);
        if (!response.data.success) {
          toast.error("Failed to fetch banner details");
          navigate("/banner");
          return;
        }
        const banner = response.data.banner;
        setFormData({
          title: banner.title || "",
          bannerType: banner.bannerType || "",
          status: banner.status || "PENDING",
          audience: banner.audience || "ALL",
          onlyActiveClass: Boolean(banner.onlyActiveClass),
          startDate: toDateTimeLocal(banner.startDate),
          endDate: toDateTimeLocal(banner.endDate),
          redirectUrl: banner.redirectUrl || "",
        });
        setImagePreview(banner.bannerImage || "");
      } catch (error) {
        console.error("Error fetching banner:", error);
        toast.error(error.response?.data?.message || "Failed to fetch banner details");
        navigate("/banner");
      } finally {
        setFetchLoading(false);
      }
    };

    fetchBannerData();
  }, [id, navigate]);

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please select a valid image file");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size should be less than 5MB");
      return;
    }
    setBannerImage(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const message = validateBannerForm(formData, { requireImage: false, hasImage: true });
    if (message) {
      toast.error(message);
      return;
    }

    setLoading(true);
    try {
      const data = new FormData();
      appendBannerFields(data, formData);
      if (bannerImage) data.append("bannerImage", bannerImage);

      const response = await api.put(`/banners/${id}`, data, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.data.success) {
        toast.success(response.data.message || "Banner updated successfully");
        navigate("/banner");
      } else {
        toast.error(response.data.message || "Failed to update banner");
      }
    } catch (error) {
      console.error("Error updating banner:", error);
      toast.error(error.response?.data?.message || "Failed to update banner");
    } finally {
      setLoading(false);
    }
  };

  if (fetchLoading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <ArgonBox py={3} display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </ArgonBox>
        <Footer />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ArgonBox py={3}>
        <Card>
          <ArgonBox p={3}>
            <BannerForm
              heading="Edit Banner"
              submitLabel="Update Banner"
              formData={formData}
              setFormData={setFormData}
              imagePreview={imagePreview}
              imageCaption={bannerImage ? "New image preview" : "Current image"}
              onImageChange={handleImageChange}
              onSubmit={handleSubmit}
              onCancel={() => navigate("/banner")}
              loading={loading}
            />
          </ArgonBox>
        </Card>
      </ArgonBox>
      <Footer />
    </DashboardLayout>
  );
}

export default EditBanner;
